import {
  AlertModal,
  Box,
  Button,
  Card,
  ConfirmationModal,
  DestructiveModal,
  Divider,
  Flex,
  Heading,
  ListItem,
  Text,
  TextButton,
  TextLink,
  Toast,
  useOpenModal,
} from '@modusign/design-system';
import { getAllDocuments } from '@modusign/openapi-client-document';
import { getDocumentList } from '@modusign/openapi-client-external-document';
import { getDepositInPublic } from '@modusign/openapi-client-payment';
import { getSubscription, useStopSubscription } from '@modusign/openapi-client-subscription';
import { getTemplates } from '@modusign/openapi-client-template';
import { listWorkspaces, useDeleteWorkspace } from '@modusign/openapi-client-user';
import moment from 'moment';
import { useEffect } from 'react';
import { useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import LoadingSpinner from '@app/components/LoadingSpinner';
import useDeposit from '@app/features/payment/hooks/useDeposit';
import { useSubscription } from '@app/features/subscription/hooks/subscription';
import isTrialPlan from '@app/features/subscription/libs/isTrialPlan';
import { fetchSubscriptionData } from '@app/features/subscription/state';
import DeleteWorkspaceTechnicalSupportModal from '@app/features/workspaceManagement/components/DeleteWorkspaceCard/DeleteWorkspaceTechnicalSupportModal';
import DescriptionListItem from '@app/features/workspaceManagement/components/DeleteWorkspaceCard/DescriptionListItem';
import { useMemberSeat } from '@app/features/workspaceManagement/member/hooks/memberSeat';
import { settingsWorkspaceRoutes } from '@app/features/workspaceManagement/routes';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { useCurrentUserRole, useWorkspace } from '@app/hooks/workspace';
import josa from '@app/libs/josa';
import workspaceService from '@app/libs/workspaceService';

const MAX_RESOURCE_COUNT = 1000;

const DELETE_DESCRIPTIONS = {
  TRIAL: [
    '모든 멤버를 삭제하고, 예비 멤버가 있는 경우 초대를 취소해 주세요.',
    '문서·외부문서·템플릿을 개인 워크스페이스로 이전하거나 삭제해 주세요.',
    '체험용 요금제(Trial)를 종료해 주세요.',
    '예치금이 있는 경우 상담을 통해 환불을 요청해 주세요.',
  ],
  NOT_TRIAL: [
    '모든 멤버를 삭제하고, 예비 멤버가 있는 경우 초대를 취소해 주세요.',
    '문서·외부문서·템플릿을 개인 워크스페이스로 이전하거나 삭제해 주세요.',
    '이용 중인 요금제 또는 예치금이 있는 경우 상담을 통해 환불을 요청해 주세요.',
  ],
};

export default function DeleteWorkspaceCard() {
  const openModal = useOpenModal();
  const workspace = useWorkspace();
  const role = useCurrentUserRole();
  const history = useHistory();

  const { currentMemberCount, invitationCount } = useMemberSeat();

  const { isLoading, isError, data, refetch } = useQuery({
    queryKey: ['deleteWorkspaceRequirement', workspace.id],
    queryFn: async () => {
      const [
        document,
        achivedDocument,
        externalDocument,
        template,
        subscription,
        deposit,
        workspaces,
      ] = await Promise.all([
        getAllDocuments(workspace.id, { limit: 1, offset: 0 }),
        getAllDocuments(workspace.id, { limit: 1, offset: 0, isHidden: true }),
        getDocumentList(workspace.id, { limit: 1, offset: 0, count: true }),
        getTemplates(workspace.id, { limit: 1, offset: 0 }),
        getSubscription(workspace.id),
        getDepositInPublic(workspace.id),
        listWorkspaces(),
      ]);

      const otherWorkspace = workspaces.workspaces.find((ws) => ws.id !== workspace.id);
      if (!otherWorkspace) {
        throw new Error('User has only current workspace.');
      }

      return {
        resourceCounts: {
          document: document.count,
          achivedDocument: achivedDocument.count,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          externalDocument: externalDocument.count!,
          template: template.count,
        },
        hasSubscriptionOrDeposit: subscription?.isSubscribing || deposit.balance !== 0,
        otherWorkspace,
        subscription,
      };
    },
  });

  useEffect(() => {
    // puiblic workspace의 최고관리자만 접근 가능
    if (role && !(workspace.type === 'PUBLIC' && role.type === 'ADMIN')) {
      openModal(
        ({ isOpen, onClose }) => (
          <AlertModal isOpen={isOpen} title="접근 안내" variant="warning" onClose={onClose}>
            해당 페이지에 접근할 수 없습니다.
          </AlertModal>
        ),
        { keepOnUnmount: true }
      );
      history.push(settingsWorkspaceRoutes.setting);
    }
  }, [history, openModal, role, workspace.type]);

  if (isLoading) {
    return (
      <Card>
        <Heading>워크스페이스 삭제</Heading>
        <Flex alignItems="center" height="200px" justifyContent="center">
          <LoadingSpinner />
        </Flex>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <Heading>워크스페이스 삭제</Heading>
        <Flex alignItems="center" flexDirection="column" height="200px" justifyContent="center">
          <Text>불러오기 실패</Text>
          <TextButton
            onClick={() => {
              refetch();
            }}
          >
            다시 시도
          </TextButton>
        </Flex>
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  const { resourceCounts, hasSubscriptionOrDeposit, otherWorkspace } = data;

  const totalResourceCount =
    resourceCounts.document +
    resourceCounts.achivedDocument +
    resourceCounts.externalDocument +
    resourceCounts.template;

  return (
    <Card>
      <Flex flexDirection="column" sx={{ gap: 6 }}>
        <Heading>워크스페이스 삭제</Heading>
        <Text as="div" color="textNeutralLight">
          공용 워크스페이스를 삭제하고 개인 워크스페이스에서 계속 모두싸인을 이용할 수 있습니다.
        </Text>
        <Divider />
        <Flex as="ul" flexDirection="column" sx={{ gap: 6 }}>
          {isTrialPlan(data.subscription.plan?.id)
            ? DELETE_DESCRIPTIONS.TRIAL.map((description, idx) => (
                <DescriptionListItem index={idx} key={description}>
                  <Text>{description}</Text>
                </DescriptionListItem>
              ))
            : DELETE_DESCRIPTIONS.NOT_TRIAL.map((description, idx) => (
                <DescriptionListItem index={idx} key={description}>
                  <Text>{description}</Text>
                </DescriptionListItem>
              ))}
        </Flex>
        <Flex justifyContent="center" my={4}>
          <Button
            variant="danger"
            onClick={() => {
              if (currentMemberCount + invitationCount > 1) {
                openModal(({ isOpen, onClose }) => (
                  <DeleteMemberAlertModal
                    counts={{ currentMemberCount, invitationCount }}
                    isOpen={isOpen}
                    onClose={onClose}
                  />
                ));
                return;
              }
              if (totalResourceCount >= MAX_RESOURCE_COUNT) {
                openModal((props) => (
                  <DeleteWorkspaceTechnicalSupportModal
                    {...props}
                    resourceCounts={data.resourceCounts}
                  />
                ));
                return;
              }
              if (totalResourceCount > 0) {
                openModal(({ onClose }) => (
                  <ResourceCountAlertModal resourceCounts={resourceCounts} onClose={onClose} />
                ));
                return;
              }

              if (hasSubscriptionOrDeposit) {
                if (isTrialPlan(data.subscription.plan?.id)) {
                  // 이용 중인 Trial 요금제가 있는 경우, 요금제 종료 안내 모달 출력
                  openModal(({ isOpen, onClose }) => (
                    <StopTrialSubscriptionModal
                      isOpen={isOpen}
                      refetch={() => refetch()}
                      onClose={onClose}
                    />
                  ));
                } else {
                  // 유료 요금제 or 예치금이 남아있는 경우, 환불 안내 모달 출력
                  openModal(RefundSubscriptionModal);
                }
                return;
              }

              // 문서·외부문서·템플릿의 개수가 0인 공용 웍스에서 이용 중인 요금제 없고, 예치금도 없는 경우, 워크스페이스 삭제 모달 출력
              openModal(({ isOpen, onClose }) => (
                <DeleteWorkspaceModal
                  isOpen={isOpen}
                  onCancel={onClose}
                  onComplete={() => {
                    workspaceService.setWorkspaceId(otherWorkspace.id);
                    Toast.success({
                      description: `${josa(workspace.name ?? '', '이/가')} 삭제되었습니다.`,
                    });
                    history.push('/');
                  }}
                />
              ));
            }}
          >
            워크스페이스 삭제 진행
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
}

function DeleteMemberAlertModal({
  counts,
  isOpen,
  onClose,
}: {
  counts: { currentMemberCount: number; invitationCount: number };
  isOpen: boolean;
  onClose: () => void;
}) {
  const history = useHistory();
  return (
    <ConfirmationModal
      closeButtonLabel="취소"
      confirmButtonLabel="멤버 관리로 이동"
      isOpen={isOpen}
      maxWidth="xs"
      title="공용 워크스페이스 삭제 안내"
      variant="warning"
      onClose={onClose}
      onConfirm={() => {
        history.push(settingsWorkspaceRoutes.members);
        onClose();
      }}
    >
      <Text as="p" textAlign="center">
        모든 멤버 삭제 후 워크스페이스를 삭제할 수 있습니다.
        <br />
        초대 중인 예비 멤버가 있는 경우 초대를 취소해 주세요.
      </Text>
      <Flex
        alignItems="flex-start"
        as="ul"
        bg="boxBackgroundNeutralBase"
        border="1px solid"
        borderColor="boxBorderNeutralBase"
        flexDirection="column"
        mt={6}
        px={6}
        py={7}
        sx={{ gap: 2 }}
      >
        <ListItem variant="square">
          <Text fontSize="sm">멤버 : {counts.currentMemberCount}명</Text>
        </ListItem>
        <ListItem variant="square">
          <Text fontSize="sm">예비 멤버 : {counts.invitationCount}명</Text>
        </ListItem>
      </Flex>
    </ConfirmationModal>
  );
}

function ResourceCountAlertModal({
  onClose,
  resourceCounts,
}: {
  resourceCounts: {
    document: number;
    achivedDocument: number;
    externalDocument: number;
    template: number;
  };
  onClose: () => void;
}) {
  return (
    <AlertModal
      isOpen
      maxWidth="xs"
      title="공용 워크스페이스 삭제 안내"
      variant="warning"
      onClose={onClose}
    >
      <Text as="p" textAlign="center">
        문서, 템플릿 등의 데이터가 남아 있습니다.
        <br />
        아래의 데이터를 개인 워크스페이스로 이전하거나 삭제해 주세요.
      </Text>
      <Flex
        alignItems="flex-start"
        as="ul"
        bg="boxBackgroundNeutralBase"
        border="1px solid"
        borderColor="boxBorderNeutralBase"
        flexDirection="column"
        gap={2}
        mt={6}
        px={6}
        py={7}
      >
        <ListItem variant="square">
          문서: {resourceCounts.document}개 (숨긴 문서 {resourceCounts.achivedDocument}개)
        </ListItem>
        <ListItem variant="square">외부 문서: {resourceCounts.externalDocument}개</ListItem>
        <ListItem variant="square">템플릿: {resourceCounts.template}개</ListItem>
      </Flex>
      <TextLink
        display="block"
        href="https://support.modusign.co.kr/131158813290"
        mt={6}
        target="_blank"
      >
        이전 방법 자세히 보기
      </TextLink>
    </AlertModal>
  );
}

function RefundSubscriptionModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const subscriptionQuery = useSubscription();
  const depositQuery = useDeposit();

  return (
    <ConfirmationModal
      closeButtonLabel="닫기"
      confirmButtonClassName="tagContactButton"
      confirmButtonLabel="상담원 연결"
      isOpen={isOpen}
      maxWidth="xs"
      title="공용 워크스페이스 삭제 안내"
      variant="warning"
      onClose={onClose}
      onConfirm={onClose}
    >
      <Text as="p" textAlign="center">
        요금제의 이용 기간 또는 예치금이 남아 있습니다.
        <br />
        환불 처리 후 공용 워크스페이스를 삭제할 수 있습니다.
        <br />
        상담원에게 환불을 요청해 주세요.
      </Text>
      <Flex
        alignItems="flex-start"
        as="ul"
        bg="boxBackgroundNeutralBase"
        border="1px solid"
        borderColor="boxBorderNeutralBase"
        flexDirection="column"
        gap={2}
        mt={6}
        px={6}
        py={7}
      >
        {depositQuery.isError ? (
          <Text fontSize="sm" sx={{ width: '100%' }} textAlign="center">
            요금제/결제 정보를 불러오지 못했습니다.
            <br />
            <TextButton
              onClick={() => {
                depositQuery.refetch();
              }}
            >
              다시 시도
            </TextButton>
          </Text>
        ) : (
          <>
            {subscriptionQuery.data?.plan ? (
              <>
                <ListItem variant="square">
                  <Text fontSize="sm">요금제 이름 : {subscriptionQuery.data.plan.title}</Text>
                </ListItem>
                <ListItem variant="square">
                  <Text fontSize="sm">
                    요금제 이용 기간 : ~{' '}
                    {moment(subscriptionQuery.data.nextInvoicingDate).format('ll')}
                  </Text>
                </ListItem>
              </>
            ) : (
              <ListItem variant="square">
                <Text fontSize="sm">이용 중인 요금제: 없음</Text>
              </ListItem>
            )}
            <ListItem variant="square">
              <Text fontSize="sm">예치금 : {depositQuery.data?.balance.toLocaleString()}원</Text>
            </ListItem>
          </>
        )}
      </Flex>
    </ConfirmationModal>
  );
}

function StopTrialSubscriptionModal({
  isOpen,
  onClose,
  refetch,
}: {
  isOpen: boolean;
  onClose: () => void;
  refetch: () => void;
}) {
  const dispatch = useDispatch();
  const workspaceId = useWorkspaceId();
  const subscriptionQuery = useSubscription();
  const { createErrorHandler } = useOpenErrorModal();

  const { mutate: stopSubscription, isLoading } = useStopSubscription({
    mutation: {
      onSuccess: () => {
        Toast.success({
          description: <Text>이용 중인 요금제가 종료되었습니다.</Text>,
        });
        refetch();
        dispatch(fetchSubscriptionData());
        onClose();
      },
      onError: createErrorHandler({ title: '요금제 종료 실패' }),
    },
  });

  return (
    <ConfirmationModal
      closeButtonLabel="닫기"
      confirmButtonLabel="요금제 종료"
      isLoading={isLoading}
      isOpen={isOpen}
      maxWidth="xs"
      title="공용 워크스페이스 삭제 안내"
      variant="warning"
      onClose={onClose}
      onConfirm={() => {
        stopSubscription({ workspaceId });
      }}
    >
      <Text as="p" textAlign="center">
        체험용 요금제(Trial)를 이용 중입니다.
        <br />
        요금제 종료 후 공용 워크스페이스를 삭제할 수 있습니다.
      </Text>
      <Flex
        alignItems="flex-start"
        as="ul"
        bg="boxBackgroundNeutralBase"
        border="1px solid"
        borderColor="boxBorderNeutralBase"
        flexDirection="column"
        gap={2}
        mt={6}
        px={6}
        py={7}
      >
        <ListItem variant="square">
          <Text fontSize="sm">
            요금제 이용 기간 : ~ {moment(subscriptionQuery.data?.nextInvoicingDate).format('ll')}
          </Text>
        </ListItem>
        <ListItem variant="square">
          <Text fontSize="sm">
            체험용 요금제를 종료하면 무료로 제공되는 혜택을 더 이상 이용할 수 없게 됩니다.
          </Text>
        </ListItem>
        <ListItem variant="square">
          <Text fontSize="sm">체험용 요금제는 최초 1회만 제공됩니다.</Text>
        </ListItem>
      </Flex>
    </ConfirmationModal>
  );
}

function DeleteWorkspaceModal({
  isOpen,
  onCancel,
  onComplete,
}: {
  isOpen: boolean;
  onCancel: () => void;
  onComplete: () => void;
}) {
  const dispatch = useDispatch();
  const workspace = useWorkspace();
  const { createErrorHandler } = useOpenErrorModal();

  const { mutate, isLoading } = useDeleteWorkspace({
    mutation: {
      onSuccess: () => {
        onComplete();
        dispatch(fetchSubscriptionData());
      },
      onError: createErrorHandler({ title: '워크스페이스 삭제 실패' }),
    },
  });

  return (
    <DestructiveModal
      confirmButtonLabel="삭제"
      isLoading={isLoading}
      isOpen={isOpen}
      maxWidth="xs"
      title="워크스페이스 삭제 안내"
      variant="destructive"
      onClose={onCancel}
      onConfirm={() => {
        mutate({ workspaceId: workspace.id });
      }}
    >
      <Text as="p" textAlign="center">
        {`'${workspace.name}'${josa.only(workspace.name || '', '을/를')} 완전히 삭제할까요?`}
      </Text>

      <Box
        as="ul"
        bg="boxBackgroundNegativeBase"
        borderColor="boxBorderNegativeBase"
        borderStyle="solid"
        borderWidth="1px"
        mt={7}
        px={6}
        py={7}
      >
        <ListItem variant="square">
          <Text>삭제한 워크스페이스는 복구할 수 없습니다.</Text>
        </ListItem>
      </Box>
    </DestructiveModal>
  );
}
